import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { ArrowLeft, Inbox, BellOff } from 'lucide-react';
import SafetyMenu from './SafetyMenu';

const Avatar = ({ src, name }) => (
  src ? (
    <img src={src} alt={name} className="w-12 h-12 rounded-full object-cover" />
  ) : (
    <div className="w-12 h-12 rounded-full bg-gradient-to-tr from-purple-400 to-pink-400 flex items-center justify-center text-white font-bold">
      {(name || '?').charAt(0).toUpperCase()}
    </div>
  )
);

const formatTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
};

const Conversations = ({
  token,
  API,
  t,
  isRTL,
  onBack,
  onOpenThread, // (otherUserId, otherUserName) => ...
  onAfterRead,
  liveEvent,
}) => {
  const [tab, setTab] = useState('active'); // active | requests
  const [active, setActive] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // conversation_id when accept/decline running

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, r] = await Promise.all([
        axios.get(`${API}/chat/conversations`, { headers }),
        axios.get(`${API}/chat/requests`, { headers }),
      ]);
      setActive(a.data || []);
      setRequests(r.data || []);
    } catch (e) {
      setActive([]);
      setRequests([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [API, token]);

  useEffect(() => {
    load();
  }, [load]);

  // Phase 19 — when a new DM lands while this screen is open, just
  // re-fetch the list. Cheap, and keeps last-message + unread badges
  // accurate without per-conversation surgery.
  useEffect(() => {
    if (!liveEvent) return;
    if (liveEvent.type === 'message_new') {
      load();
    }
  }, [liveEvent, load]);

  const acceptReq = async (cid) => {
    setBusy(cid);
    try {
      await axios.post(`${API}/chat/requests/${cid}/accept`, {}, { headers });
      await load();
    } finally {
      setBusy(null);
    }
  };

  const declineReq = async (cid) => {
    setBusy(cid);
    try {
      await axios.post(`${API}/chat/requests/${cid}/decline`, {}, { headers });
      await load();
    } finally {
      setBusy(null);
    }
  };

  const toggleMute = async (c) => {
    setBusy(c.conversation_id);
    try {
      if (c.muted) {
        await axios.delete(`${API}/chat/conversations/${c.conversation_id}/mute`, { headers });
      } else {
        await axios.post(`${API}/chat/conversations/${c.conversation_id}/mute`, {}, { headers });
      }
      setActive((arr) =>
        arr.map((x) => (x.conversation_id === c.conversation_id ? { ...x, muted: !c.muted } : x))
      );
    } finally {
      setBusy(null);
    }
  };

  const hideConvo = async (c) => {
    if (!window.confirm(t('hideConvoConfirm'))) return;
    setBusy(c.conversation_id);
    try {
      await axios.post(`${API}/chat/conversations/${c.conversation_id}/hide`, {}, { headers });
      setActive((arr) => arr.filter((x) => x.conversation_id !== c.conversation_id));
    } finally {
      setBusy(null);
    }
  };

  const blockUser = async (c) => {
    if (!window.confirm(t('blockConfirm'))) return;
    setBusy(c.conversation_id);
    try {
      await axios.post(`${API}/users/${c.other_user_id}/block`, {}, { headers });
      setActive((arr) => arr.filter((x) => x.conversation_id !== c.conversation_id));
    } catch {
      alert(t('error'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="conversations-screen" dir={isRTL ? 'rtl' : 'ltr'} className="min-h-screen bg-gray-50">
      <header className="sticky top-0 z-30 bg-white border-b border-gray-100">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={onBack}
            data-testid="conversations-back"
            className="flex items-center gap-1 text-purple-600 font-medium"
          >
            <ArrowLeft className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
            <span className="text-sm">{t('back')}</span>
          </button>
          <h1 className="text-base font-bold text-gray-900">{t('messages')}</h1>
          <div className="w-12" />
        </div>
        <div className="max-w-2xl mx-auto px-4 pb-3">
          <div className="flex bg-gray-100 rounded-xl p-1">
            <button
              onClick={() => setTab('active')}
              data-testid="conversations-tab-active"
              className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors ${
                tab === 'active' ? 'bg-white text-purple-700 shadow' : 'text-gray-500'
              }`}
            >
              {t('activeChats')} {active.length > 0 && (
                <span className="ms-1 text-xs">({active.length})</span>
              )}
            </button>
            <button
              onClick={() => setTab('requests')}
              data-testid="conversations-tab-requests"
              className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors ${
                tab === 'requests' ? 'bg-white text-purple-700 shadow' : 'text-gray-500'
              }`}
            >
              {t('requestsTab')}{' '}
              {requests.length > 0 && (
                <span className="ms-1 text-xs">({requests.length})</span>
              )}
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-3 py-3 pb-24">
        {loading ? (
          <div className="py-16 text-center text-gray-500">{t('loading')}</div>
        ) : tab === 'active' ? (
          active.length === 0 ? (
            <div data-testid="conversations-active-empty" className="py-16 text-center text-gray-500 px-6">
              <div className="w-14 h-14 mx-auto rounded-full bg-purple-100 flex items-center justify-center text-purple-600 mb-3">
                <Inbox className="w-6 h-6" />
              </div>
              <p className="text-base font-medium text-gray-800">{t('noConversations')}</p>
              <p className="text-sm text-gray-500 mt-1">{t('noConversationsHint')}</p>
            </div>
          ) : (
            <ul className="bg-white rounded-xl border border-gray-200 overflow-hidden divide-y divide-gray-100">
              {active.map((c) => (
                <li key={c.conversation_id} className="relative">
                  <div className="flex items-center">
                    <button
                      onClick={() => onOpenThread(c.other_user_id, c.other_user_name)}
                      data-testid={`conversation-${c.other_user_id}`}
                      className="flex-1 flex items-center gap-3 px-3 py-3 hover:bg-gray-50 text-start"
                    >
                      <Avatar src={c.other_user_profile_picture_url} name={c.other_user_name} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-gray-900 truncate">
                            {c.other_user_name}
                          </p>
                          {c.muted && (
                            <BellOff
                              data-testid={`conversation-muted-${c.other_user_id}`}
                              className="w-3.5 h-3.5 text-amber-500 shrink-0"
                            />
                          )}
                          <span className="ms-auto text-[11px] text-gray-400 shrink-0">
                            {formatTime(c.last_message_at)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 truncate mt-0.5">
                          {c.last_message || '—'}
                        </p>
                      </div>
                      {c.unread_count > 0 && (
                        <span
                          data-testid={`conversation-unread-${c.other_user_id}`}
                          className="ms-1 min-w-[20px] h-5 px-1.5 rounded-full bg-purple-600 text-white text-[11px] font-bold flex items-center justify-center"
                        >
                          {c.unread_count}
                        </span>
                      )}
                    </button>
                    <div className="px-2">
                      <SafetyMenu
                        testid={`conversation-safety-${c.other_user_id}`}
                        t={t}
                        isRTL={isRTL}
                        align="end"
                        size="md"
                        items={[
                          {
                            key: 'mute',
                            label: c.muted ? t('unmute') : t('mute'),
                            onClick: () => toggleMute(c),
                            testid: `conversation-safety-mute-${c.other_user_id}`,
                          },
                          {
                            key: 'hide',
                            label: t('hideConvo'),
                            onClick: () => hideConvo(c),
                            testid: `conversation-safety-hide-${c.other_user_id}`,
                          },
                          {
                            key: 'block',
                            label: t('block'),
                            danger: true,
                            onClick: () => blockUser(c),
                            testid: `conversation-safety-block-${c.other_user_id}`,
                          },
                        ]}
                      />
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )
        ) : requests.length === 0 ? (
          <div data-testid="conversations-requests-empty" className="py-16 text-center text-gray-500 px-6">
            <div className="w-14 h-14 mx-auto rounded-full bg-amber-100 flex items-center justify-center text-amber-600 mb-3">
              <Inbox className="w-6 h-6" />
            </div>
            <p className="text-base font-medium text-gray-800">{t('noMessageRequests')}</p>
            <p className="text-sm text-gray-500 mt-1">{t('noMessageRequestsHint')}</p>
          </div>
        ) : (
          <ul className="space-y-2">
            {requests.map((r) => (
              <li
                key={r.conversation_id}
                data-testid={`request-${r.conversation_id}`}
                className="bg-white rounded-xl border border-gray-200 p-3"
              >
                <div className="flex items-center gap-3">
                  <Avatar src={r.requester_profile_picture_url} name={r.requester_name} />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 truncate">{r.requester_name}</p>
                    <p className="text-sm text-gray-600 truncate mt-0.5">"{r.first_message}"</p>
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => acceptReq(r.conversation_id)}
                    disabled={busy === r.conversation_id}
                    data-testid={`request-accept-${r.conversation_id}`}
                    className="flex-1 py-2 rounded-lg bg-purple-600 text-white text-sm font-semibold hover:bg-purple-700 disabled:opacity-50"
                  >
                    {t('accept')}
                  </button>
                  <button
                    onClick={() => declineReq(r.conversation_id)}
                    disabled={busy === r.conversation_id}
                    data-testid={`request-decline-${r.conversation_id}`}
                    className="flex-1 py-2 rounded-lg border border-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-50 disabled:opacity-50"
                  >
                    {t('decline')}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default Conversations;
